import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  CreateImmunizationInput,
  UpdateImmunizationInput,
} from "@shared/validation/immunization.schema";
import { apiClient, ApiError } from "./client";

export interface ImmunizationSummary {
  id: string;
  childId: string;
  vaccineName: string;
  dateAdministered: string | null;
  nextDueDate: string | null;
  provider: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

const immunizationsKey = (childId: string) =>
  ["children", childId, "immunizations"] as const;

export function useImmunizations(childId: string | undefined) {
  return useQuery<ImmunizationSummary[], ApiError>({
    queryKey: childId
      ? immunizationsKey(childId)
      : ["children", "__none__", "immunizations"],
    queryFn: () => apiClient<ImmunizationSummary[]>(`/api/children/${childId}/immunizations`),
    enabled: Boolean(childId),
  });
}

export function useCreateImmunization(childId: string) {
  const queryClient = useQueryClient();
  return useMutation<ImmunizationSummary, ApiError, CreateImmunizationInput>({
    mutationFn: (vars) =>
      apiClient<ImmunizationSummary>(`/api/children/${childId}/immunizations`, {
        method: "POST",
        body: JSON.stringify(vars),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: immunizationsKey(childId) });
    },
  });
}

export function useUpdateImmunization(childId: string, immunizationId: string) {
  const queryClient = useQueryClient();
  return useMutation<ImmunizationSummary, ApiError, UpdateImmunizationInput>({
    mutationFn: (vars) =>
      apiClient<ImmunizationSummary>(
        `/api/children/${childId}/immunizations/${immunizationId}`,
        {
          method: "PUT",
          body: JSON.stringify(vars),
        },
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: immunizationsKey(childId) });
    },
  });
}

export function useDeleteImmunization(childId: string) {
  const queryClient = useQueryClient();
  return useMutation<{ deleted: true }, ApiError, string>({
    mutationFn: (immunizationId) =>
      apiClient<{ deleted: true }>(
        `/api/children/${childId}/immunizations/${immunizationId}`,
        {
          method: "DELETE",
        },
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: immunizationsKey(childId) });
    },
  });
}
